<template>
    <div class="d-flex gap-5 flex-column">
        <EChart
            :options="chartData1"
            :header="$t('changeOfAveragePriceOverTime')"
            no-padding
            card
            :loading="loading"
            class="shadow-none m-0"
            type="time"
            :group-by="currentGroupBy"
        >
            <template #nav>
                <div :key="spaceId" class="d-flex gap-3 flex-column">
                    <DropdownFilter v-model="currentGroupBy" :items="['day', 'month', 'quarter', 'year']" :prefix="`${$t('range')}: `" :disabled="loading" />
                </div>
            </template>
        </EChart>
        <EChart
            :options="chartData2"
            no-padding
            card
            :loading="loading"
            class="shadow-none m-0"
        />
    </div>
</template>



<script>
    import { startOfDay, startOfMonth, startOfQuarter, startOfYear } from 'date-fns';
    import { generateEChartData } from '@/utils/chart';
    import EChart from '@/components/common/EChart.vue';
    import DropdownFilter from '@/components/common/DropdownFilter.vue';

    export default {
        name: 'ChangeOfAveragePriceOverTime',
        components: {
            EChart,
            DropdownFilter,
        },
        inject: {
            parentComponent: {
                default: null,
            },
        },
        data() {
            return {
                requestReportSet: this.parentComponent.requestReportSet,
                requestReport: this.parentComponent.requestReport,
                currentGroupBy: 'day',
            };
        },
        computed: {
            spaceId() {
                return this.$store.state.space.currentSpaceId;
            },
            timeRange() {
                return this.parentComponent.timeRange;
            },
            loading() {
                return this.parentComponent.loading;
            },
            error() {
                return this.parentComponent.error;
            },
            productSet() {
                return this.parentComponent.productSet;
            },
            currentItem() {
                return this.parentComponent.currentItem;
            },
            currentSet() {
                if (!this.currentItem?.reportId) return null;
                return (this.productSet || []).find(setEntry => setEntry.reportId === this.currentItem.reportId) || null;
            },
            productsInSet() {
                return this.currentSet?.allProducts || [];
            },
            /** Wiersze z wyliczonymi metrykami dla wykresu 2 (min/avg/max) */
            rowsForCharts() {
                if (!this.productsInSet.length) return [];

                const reportData = this.requestReport.getData();
                const endDate = this.timeRange?.endDate;

                return this.productsInSet
                    .map(productMeta => {
                        // budujemy unikalny klucz id+type
                        const uniqueKey = `${productMeta.id}_${productMeta.type}`;

                        const row = this.parentComponent.getDataForSet(this.productsInSet, reportData, endDate, productMeta.id, productMeta.type);

                        if (!row) return null;

                        // nadpisujemy name, żeby w wykresie były widoczne dwa różne wpisy
                        return {
                            ...row,
                            _uniqueKey: uniqueKey,
                            name: `${row.name} (${productMeta.type})`,
                        };
                    })
                    .filter(Boolean);
            },
            chartData1() {
                if (!this.productsInSet.length) return { series: [] };

                const reportData = this.requestReport?.getData();
                if (!reportData) return { series: [] };

                const getBucketStartTimestamp = (dateInput, groupBy) => {
                    const originalDate = new Date(dateInput);
                    switch (groupBy) {
                        case 'day':
                            return startOfDay(originalDate).getTime();
                        case 'month':
                            return startOfMonth(originalDate).getTime();
                        case 'quarter':
                            return startOfQuarter(originalDate).getTime();
                        case 'year':
                            return startOfYear(originalDate).getTime();
                        default:
                            return startOfDay(originalDate).getTime();
                    }
                };

                const series = [];

                // Dla każdego produktu twórz serię liniową z uśrednieniem per kubełek
                this.productsInSet.forEach(productMeta => {
                    const productData = productMeta.type === 'global' ? reportData.aggregations?.[productMeta.id] : reportData.products?.[productMeta.id];

                    if (!productData?.price) return;

                    // 1) Średnia dzienna
                    const dailyPoints = productData.price
                        .map(dayObj => {
                            const dateStr = dayObj.date;
                            let sum = 0;
                            let count = 0;
                            Object.entries(dayObj).forEach(([key, val]) => {
                                if (key !== 'date' && val !== null) {
                                    sum += val;
                                    count += 1;
                                }
                            });
                            const avg = count > 0 ? sum / count : null;
                            return { dateStr, avg };
                        })
                        .filter(point => point.avg !== null);

                    if (!dailyPoints.length) return;

                    // 2) Agregacja do kubełków
                    const buckets = new Map();
                    for (const point of dailyPoints) {
                        const bucketTs = getBucketStartTimestamp(point.dateStr, this.currentGroupBy);
                        const agg = buckets.get(bucketTs) || { sum: 0, count: 0 };
                        agg.sum += point.avg;
                        agg.count += 1;
                        buckets.set(bucketTs, agg);
                    }

                    // 3) Średnia per kubełek + sort
                    const groupedPoints = Array.from(buckets.entries())
                        .map(([bucketTs, agg]) => [bucketTs, agg.count > 0 ? agg.sum / agg.count : null])
                        .filter(([_, value]) => value !== null)
                        .sort((a, b) => a[0] - b[0]);

                    // 4) Dane do wykresu
                    let dataForChart;
                    if (this.currentGroupBy === 'day') {
                        dataForChart = generateEChartData(this.parentComponent.parentComponent.timeRange, groupedPoints).filter(point => {
                            const hasTwo = Array.isArray(point) && point.length >= 2;
                            const value = hasTwo ? point[1] : null;
                            return hasTwo && value !== null && Number.isFinite(Number(value));
                        });
                    } else {
                        dataForChart = groupedPoints.filter(([_, value]) => value !== null && Number.isFinite(Number(value)));
                    }

                    if (!dataForChart.length) return;

                    series.push({
                        name: productMeta.name,
                        type: 'line',
                        areaStyle: null,
                        data: dataForChart,
                    });
                });

                return {
                    tooltip: {
                        trigger: 'axis',
                        valueFormatter: value => (value ? this.$format.currency(value) : '-'),
                    },
                    yAxis: { type: 'value', name: this.$t('price') },
                    series,
                };
            },
            chartData2() {
                if (!this.rowsForCharts.length) return { series: [] };

                const categories = this.rowsForCharts.map(row => row.name);
                const minPrices = this.rowsForCharts.map(row => row.minPrice);
                const avgPrices = this.rowsForCharts.map(row => row.avgPrice);
                const maxPrices = this.rowsForCharts.map(row => row.maxPrice);

                return {
                    tooltip: {
                        trigger: 'axis',
                        valueFormatter: value => this.$format.currency(value),
                    },
                    legend: {
                        data: [this.$t('minPrice'), this.$t('avgPrice'), this.$t('maxPrice')],
                    },
                    xAxis: {
                        type: 'category',
                        data: categories,
                        axisLabel: { rotate: 10 },
                    },
                    yAxis: {
                        type: 'value',
                        name: this.$t('price'),
                    },
                    series: [
                        { name: this.$t('minPrice'), data: minPrices, type: 'bar', color: '#d8b4fe' },
                        { name: this.$t('avgPrice'), data: avgPrices, type: 'bar', color: '#7e22ce' },
                        { name: this.$t('maxPrice'), data: maxPrices, type: 'bar', color: '#c084fc' },
                    ],
                };
            },
        },
    };
</script>
